import React, { useState, useEffect, useMemo } from 'react';
import { ResponsiveBump } from '@nivo/bump';
import { TrendingUp, TrendingDown, Info, BarChart3, AlertCircle } from 'lucide-react';
import { getECIRankingData } from '../services/criticalMineralsApi';
import { useTheme } from '../context/ThemeContext';

const ECIBumpChart = () => {
  const { isDarkMode } = useTheme();
  const [selectedMinerals, setSelectedMinerals] = useState(['Lithium']); // Array for multiple selection
  const [isMultipleMode, setIsMultipleMode] = useState(false); // Track if multiple mode is active
  const [displayMode, setDisplayMode] = useState('rank'); // 'rank' or 'score'
  const [partnerLimit, setPartnerLimit] = useState('top-10'); // 'top-10' or 'all'
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dynamic Nivo theme function
  const getNivoTheme = (isDark) => {
    return {
      background: 'transparent',
      textColor: isDark ? '#ffffff' : '#1f2937',
      fontSize: 11,
      axis: {
        domain: {
          line: {
            stroke: isDark ? '#4b5563' : '#d1d5db',
            strokeWidth: 1
          }
        },
        ticks: {
          line: {
            stroke: isDark ? '#6b7280' : '#9ca3af',
            strokeWidth: 1
          },
          text: {
            fill: isDark ? '#9ca3af' : '#6b7280'
          }
        },
        legend: {
          text: {
            fill: isDark ? '#ffffff' : '#1f2937'
          }
        }
      },
      grid: {
        line: {
          stroke: isDark ? '#374151' : '#e5e7eb',
          strokeWidth: 1
        }
      },
      tooltip: {
        container: {
          background: isDark ? '#1f2937' : '#ffffff',
          color: isDark ? '#ffffff' : '#1f2937',
          fontSize: '12px',
          borderRadius: '6px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          border: isDark ? '1px solid #374151' : '1px solid #d1d5db'
        }
      }
    };
  };

  // Mineral color mapping for themes (updated to match provided colors)
  const mineralColors = {
    'Lithium': '#ef4444', // red
    'Cobalt': '#3b82f6', // blue
    'Nickel': '#10b981', // emerald
    'Graphite': '#a855f7', // violet
    'Rare Earth Elements': '#f59e0b', // amber
    'All': '#6b7280' // neutral gray for multiple selection
  };

  // Neutral palette for multiple mineral combinations
  const neutralPalette = [
    "#ef4444","#3b82f6","#10b981","#a855f7","#f59e0b",
    "#06b6d4","#8b5cf6","#22c55e","#eab308","#f97316",
    "#64748b","#0ea5e9","#34d399","#f472b6","#94a3b8"
  ];

  // Category10 color scheme to match Nivo's default
  const category10Colors = [
    "#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd",
    "#8c564b", "#e377c2", "#7f7f7f", "#bcbd22", "#17becf"
  ];

  // ECI color scale matching the legend
  const eciColorScale = [
    '#2b54ac', // Blue (best ranking)
    '#95aae9', // Light blue
    '#f5f4fc', // Very light purple
    '#d6e2bc', // Light green
    '#f59e0b', // Amber
    '#df4f44'  // Red (worst ranking)
  ];

  // Function to get color based on ECI value
  const getECIColor = (value, isRank = true) => {
    if (value === null || value === undefined) return '#6b7280'; // Gray for missing data
    
    if (isRank) {
      // For ranking: lower is better (1 = best, 100 = worst)
      if (value <= 10) return eciColorScale[0]; // Blue
      if (value <= 20) return eciColorScale[1]; // Light blue
      if (value <= 40) return eciColorScale[2]; // Very light purple
      if (value <= 60) return eciColorScale[3]; // Light green
      if (value <= 80) return eciColorScale[4]; // Amber
      return eciColorScale[5]; // Red
    } else {
      // For ECI score: higher is better
      if (value >= 2.0) return eciColorScale[0]; // Blue
      if (value >= 1.5) return eciColorScale[1]; // Light blue
      if (value >= 1.0) return eciColorScale[2]; // Very light purple
      if (value >= 0.5) return eciColorScale[3]; // Light green
      if (value >= 0.0) return eciColorScale[4]; // Amber
      return eciColorScale[5]; // Red
    }
  };

  // Get colors for the chart based on current selection
  const getChartColors = () => {
    if (selectedMinerals.length > 1) {
      return neutralPalette;
    }
    return category10Colors;
  };

  // Available minerals
  const availableMinerals = ['Lithium', 'Cobalt', 'Nickel', 'Graphite', 'Rare Earth Elements'];

  // Get current theme color based on selected minerals
  const getCurrentThemeColor = () => {
    if (selectedMinerals.length === 1 && !isMultipleMode) {
      return mineralColors[selectedMinerals[0]] || mineralColors['All'];
    }
    return mineralColors['All']; // Neutral theme for multiple minerals
  };

  const handleMineralToggle = (mineral) => {
    setSelectedMinerals(prev => {
      const newSelection = prev.includes(mineral) 
        ? prev.filter(m => m !== mineral)
        : [...prev, mineral];
      
      // Update multiple mode based on selection
      setIsMultipleMode(newSelection.length > 1);
      
      return newSelection.length > 0 ? newSelection : ['Lithium']; // Ensure at least one is selected
    });
  };

  // Backend now handles all aggregation logic - no frontend aggregation needed

  // Memoize the processed data to prevent unnecessary re-renders
  const processedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    // Ensure data is properly formatted and mutable
    let processed = data.map(country => {
      if (!country || typeof country !== 'object') {
        console.warn('Invalid country data:', country);
        return null;
      }
      
      return {
        ...country,
        id: country.id || 'Unknown',
        data: country.data && Array.isArray(country.data) 
          ? country.data
              .map(point => ({ 
                ...point,
                x: String(point.x || '2023'), // Ensure x is string for Nivo
                y: Number(point.y || 0), // Ensure y is number
                eci: Number(point.eci || 0)
              }))
              .sort((a, b) => parseInt(a.x) - parseInt(b.x)) // Sort years numerically
          : []
      };
    }).filter(Boolean); // Remove null entries
    
    // Apply frontend filtering as backup
    const maxCountries = partnerLimit === 'top-10' ? 10 : 20;
    processed = processed.slice(0, maxCountries);
    
    console.log(`Processed data for chart (limited to ${maxCountries}):`, processed);
    
    // Debug: Check year ordering in processed data
    if (processed.length > 0) {
      console.log('=== YEAR ORDERING DEBUG ===');
      processed.forEach((country, index) => {
        if (index < 3) { // Only log first 3 countries
          console.log(`${country.id} years:`, country.data.map(d => d.x));
        }
      });
    }
    
    return processed;
  }, [data, partnerLimit]);

  // Get all unique years from the data and sort them
  const allYears = useMemo(() => {
    const years = new Set();
    processedData.forEach(country => {
      country.data.forEach(point => {
        years.add(point.x);
      });
    });
    const sortedYears = Array.from(years).sort((a, b) => parseInt(a) - parseInt(b));
    console.log('=== CALCULATED YEARS FOR AXIS ===', sortedYears);
    return sortedYears;
  }, [processedData]);

  // Force data to have consistent year ordering and ensure all years are present
  const orderedData = useMemo(() => {
    // First, process all countries and get their data
    let processed = processedData.map(country => {
      const sortedData = country.data.sort((a, b) => parseInt(a.x) - parseInt(b.x));
      
      // Ensure all years are present in the data
      const allYearsSet = new Set(allYears);
      const existingYears = new Set(sortedData.map(point => point.x));
      const missingYears = allYears.filter(year => !existingYears.has(year));
      
      // Add missing years with interpolated values or last known value
      const completeData = [...sortedData];
      missingYears.forEach(year => {
        // Find the last known value before this year
        const lastKnownValue = sortedData
          .filter(point => parseInt(point.x) < parseInt(year))
          .sort((a, b) => parseInt(b.x) - parseInt(a.x))[0];
        
        // Find the next known value after this year
        const nextKnownValue = sortedData
          .filter(point => parseInt(point.x) > parseInt(year))
          .sort((a, b) => parseInt(a.x) - parseInt(b.x))[0];
        
        let interpolatedValue = null;
        if (lastKnownValue && nextKnownValue) {
          // Linear interpolation
          const timeDiff = parseInt(nextKnownValue.x) - parseInt(lastKnownValue.x);
          const valueDiff = nextKnownValue.y - lastKnownValue.y;
          const timeRatio = (parseInt(year) - parseInt(lastKnownValue.x)) / timeDiff;
          interpolatedValue = Math.round(lastKnownValue.y + (valueDiff * timeRatio));
        } else if (lastKnownValue) {
          // Use last known value
          interpolatedValue = lastKnownValue.y;
        } else if (nextKnownValue) {
          // Use next known value
          interpolatedValue = nextKnownValue.y;
        }
        
        completeData.push({
          x: year,
          y: interpolatedValue,
          eci: interpolatedValue ? (lastKnownValue?.eci || nextKnownValue?.eci || 0) : null
        });
      });
      
      // Calculate average ECI value for this country to determine color
      const validValues = completeData.filter(point => point.y !== null);
      const avgValue = validValues.length > 0 
        ? validValues.reduce((sum, point) => sum + point.y, 0) / validValues.length 
        : null;
      
      return {
        ...country,
        data: completeData.sort((a, b) => parseInt(a.x) - parseInt(b.x)),
        color: getECIColor(avgValue, displayMode === 'rank')
      };
    });

    // For score mode, negate y values to flip the chart (higher scores at top)
    // Bump charts show lower y values at top, so negating will flip it
    if (displayMode === 'score') {
      processed = processed.map(country => ({
        ...country,
        data: country.data.map(point => ({
          ...point,
          y: point.y !== null ? -point.y : null,
          originalY: point.y // Store original for axis formatting
        }))
      }));
    }

    return processed;
  }, [processedData, allYears, displayMode]);

  useEffect(() => {
    const fetchECIData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Backend now handles both single and multiple minerals with proper aggregation
        const mineralParam = selectedMinerals.length === 1 ? selectedMinerals[0] : selectedMinerals.join(',');
        console.log('Fetching ECI data with params:', { partnerLimit, mineralParam, displayMode, selectedMinerals });
        
        const response = await getECIRankingData(partnerLimit, mineralParam, displayMode);
        
        if (response.success && response.data) {
          const clonedData = JSON.parse(JSON.stringify(response.data));
          console.log('API data received:', clonedData);
          console.log('Summary:', response.summary);
          console.log('First country in API response:', clonedData[0]);
          console.log('First country keys:', clonedData[0] ? Object.keys(clonedData[0]) : 'No data');
          setData(clonedData);
        } else if (response.data) {
          // Handle case where response doesn't have success flag but has data
          const clonedData = JSON.parse(JSON.stringify(response.data));
          console.log('API data received (no success flag):', clonedData);
          setData(clonedData);
        } else {
          console.error('API failed to return data:', response);
          setError(response.message || 'Failed to load ECI ranking data from backend');
          setData([]);
        }
      } catch (error) {
        console.error('Error fetching ECI data:', error);
        setError(error.message);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    if (selectedMinerals.length > 0) {
      fetchECIData();
    }
  }, [selectedMinerals, displayMode, partnerLimit]);


  const getRankingTrend = (countryData) => {
    const firstRank = countryData[0].y;
    const lastRank = countryData[countryData.length - 1].y;
    const change = firstRank - lastRank;
    
    if (change > 0) return { trend: 'improved', change: change, icon: TrendingUp, color: 'text-green-600' };
    if (change < 0) return { trend: 'declined', change: Math.abs(change), icon: TrendingDown, color: 'text-red-600' };
    return { trend: 'stable', change: 0, icon: TrendingUp, color: 'text-gray-600' };
  };

  // Calculate ECI KPIs based on current data
  const calculateECIKPIs = useMemo(() => {
    if (!processedData || processedData.length === 0) return null;
    
    // Check if we have valid data
    if (processedData.length === 0) {
      console.log('ECI KPI Debug - No processed data available');
      return null;
    }
    
    // Calculate avg_import_share from data points if not available at country level
    const countriesWithImportData = processedData.map(country => {
      if (country.avg_import_share) {
        return country; // Already has avg_import_share
      }
      
      // Calculate average from data points
      if (country.data && country.data.length > 0) {
        const totalImportShare = country.data.reduce((sum, point) => sum + (point.import_share || 0), 0);
        const avgImportShare = totalImportShare / country.data.length;
        return {
          ...country,
          avg_import_share: avgImportShare
        };
      }
      
      return {
        ...country,
        avg_import_share: 0
      };
    });
    
    // Check if import share values exist and are non-zero
    const validCountries = countriesWithImportData.filter(country => (country.avg_import_share || 0) > 0);
    console.log(`ECI KPI Debug - Countries with import shares: ${validCountries.length}/${countriesWithImportData.length}`);
    
    // Use valid countries if available, otherwise use all countries for display
    const countriesToUse = validCountries.length > 0 ? validCountries : countriesWithImportData;
    
    console.log(`ECI KPI Debug - Using ${countriesToUse.length} countries for calculations`);
    
    // Debug: Check the actual data structure of the first country
    if (countriesToUse.length > 0) {
      console.log('ECI KPI Debug - First country data structure:', countriesToUse[0]);
      console.log('ECI KPI Debug - First country avg_import_share:', countriesToUse[0].avg_import_share);
      console.log('ECI KPI Debug - First country keys:', Object.keys(countriesToUse[0]));
    }
    
    // Helper function to get ECI complexity level
    const getECIComplexity = (eci) => {
      if (eci >= 1.5) return "very high complexity";
      if (eci >= 1.0) return "high complexity";
      if (eci >= 0.5) return "moderate complexity";
      if (eci >= 0.0) return "low complexity";
      return "very low complexity";
    };
    
    // ============================================
    // KPI 1: Average ECI of Supplier Countries (Weighted by import share)
    // ============================================
    let totalWeightedECI = 0;
    let totalWeight = 0;
    
    countriesToUse.forEach(country => {
      // Get the latest ECI value from the data array
      const latestDataPoint = country.data && country.data.length > 0 
        ? country.data[country.data.length - 1] 
        : null;
      
      const weight = country.avg_import_share || 0;
      const eci = latestDataPoint ? (latestDataPoint.eci || 0) : 0;
      
      totalWeightedECI += (eci * weight);
      totalWeight += weight;
    });
    
    const avgECI = totalWeight > 0 ? totalWeightedECI / totalWeight : 0;
    
    // ============================================
    // KPI 2: Dependency on Low-Complexity Economies (ECI < 0.0)
    // ============================================
    const lowComplexityThreshold = 0.0;
    let totalImports = 0;
    let lowComplexityImports = 0;
    
    countriesToUse.forEach(country => {
      const importShare = country.avg_import_share || 0;
      totalImports += importShare;
      
      // Get the latest ECI value from the data array
      const latestDataPoint = country.data && country.data.length > 0 
        ? country.data[country.data.length - 1] 
        : null;
      const eci = latestDataPoint ? (latestDataPoint.eci || 0) : 0;
      
      if (eci < lowComplexityThreshold) {
        lowComplexityImports += importShare;
      }
    });
    
    const lowComplexityPercentage = totalImports > 0 
      ? (lowComplexityImports / totalImports * 100) 
      : 0;
    
    const lowComplexityCountries = countriesToUse
      .filter(c => {
        const latestDataPoint = c.data && c.data.length > 0 
          ? c.data[c.data.length - 1] 
          : null;
        const eci = latestDataPoint ? (latestDataPoint.eci || 0) : 0;
        return eci < lowComplexityThreshold;
      })
      .sort((a, b) => (b.avg_import_share || 0) - (a.avg_import_share || 0))
      .slice(0, 2);
    
    // ============================================
    // KPI 3: Share of Imports from High-ECI Nations (ECI > 1.0)
    // ============================================
    const highECIThreshold = 1.0;
    let highECIImports = 0;
    
    countriesToUse.forEach(country => {
      const importShare = country.avg_import_share || 0;
      
      // Get the latest ECI value from the data array
      const latestDataPoint = country.data && country.data.length > 0 
        ? country.data[country.data.length - 1] 
        : null;
      const eci = latestDataPoint ? (latestDataPoint.eci || 0) : 0;
      
      if (eci > highECIThreshold) {
        highECIImports += importShare;
      }
    });
    
    const highECIPercentage = totalImports > 0 
      ? (highECIImports / totalImports * 100) 
      : 0;
    
    const highECICountries = countriesToUse
      .filter(c => {
        const latestDataPoint = c.data && c.data.length > 0 
          ? c.data[c.data.length - 1] 
          : null;
        const eci = latestDataPoint ? (latestDataPoint.eci || 0) : 0;
        return eci > highECIThreshold;
      })
      .sort((a, b) => (b.avg_import_share || 0) - (a.avg_import_share || 0))
      .slice(0, 2);
    
    return {
      avgECI: {
        value: avgECI.toFixed(2),
        complexity: getECIComplexity(avgECI),
        totalCountries: countriesToUse.length,
        hasImportData: validCountries.length > 0
      },
      lowComplexity: {
        percentage: lowComplexityPercentage.toFixed(1),
        countries: lowComplexityCountries,
        threshold: lowComplexityThreshold,
        hasImportData: validCountries.length > 0
      },
      highECI: {
        percentage: highECIPercentage.toFixed(1),
        countries: highECICountries,
        threshold: highECIThreshold,
        hasImportData: validCountries.length > 0
      }
    };
  }, [processedData]);


  if (loading) {
    const themeColor = getCurrentThemeColor();
    return (
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 p-6 transition-colors duration-200">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg" style={{ backgroundColor: themeColor }}>
            <BarChart3 className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Economic Complexity Index (ECI) Rankings
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Loading ECI ranking data from backend...
            </p>
          </div>
        </div>
        <div className="animate-pulse">
          <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
      </div>
    );
  }

  const themeColor = getCurrentThemeColor();

  return (
    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 transition-colors duration-200">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-600">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Economic Complexity Index (ECI) Rankings
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {selectedMinerals.length > 1 
                  ? `Aggregated view across ${selectedMinerals.join(', ')} (${selectedMinerals.length} minerals)` 
                  : `${selectedMinerals[0]} suppliers complexity over time`}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
              {/* <Info className="h-3 w-3" /> */}
              {/* <span>
                {displayMode === 'rank' 
                  ? 'Lower ranking = Higher complexity (rankings change over time)' 
                  : 'Higher score = Higher complexity (ECI scores are country constants)'}
              </span> */}
            </div>
          </div>
        </div>

        {/* ECI KPI Summary Cards - Moved here, right after heading */}
        {calculateECIKPIs && (
          <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            
            {/* KPI 1: Average Supplier ECI */}
            <div className="kpi-card" style={{ borderColor: 'var(--kpi-border)' }}>
              <div className="mb-3">
                <div className="kpi-label" style={{ color: 'var(--kpi-text)' }}>Average Supplier ECI</div>
              </div>
              
              <div className="kpi-value">
                {calculateECIKPIs.avgECI.value}
              </div>
              
              <div className="kpi-metric" style={{ color: 'var(--kpi-text)' }}>
                {calculateECIKPIs.avgECI.complexity}
              </div>
              
              <div className="text-gray-600 dark:text-gray-400 text-xs mt-2">
                {calculateECIKPIs.avgECI.hasImportData 
                  ? `Weighted by import share • ${calculateECIKPIs.avgECI.totalCountries} suppliers`
                  : `No import data available • ${calculateECIKPIs.avgECI.totalCountries} suppliers`
                }
              </div>
            </div>

            {/* KPI 2: Low-Complexity Dependency */}
            <div className="kpi-card" style={{ borderColor: 'var(--kpi-border)' }}>
              <div className="mb-3">
                <div className="kpi-label" style={{ color: 'var(--kpi-text)' }}>Low-Complexity Dependency</div>
              </div>
              
              <div className="kpi-value">
                {calculateECIKPIs.lowComplexity.percentage}%
              </div>
              
              <div className="kpi-metric" style={{ color: 'var(--kpi-text)' }}>
                {calculateECIKPIs.lowComplexity.hasImportData 
                  ? `From economies below ${calculateECIKPIs.lowComplexity.threshold} ECI`
                  : `No import data available`
                }
              </div>
              
              {/* Top low-complexity suppliers */}
              {calculateECIKPIs.lowComplexity.hasImportData && calculateECIKPIs.lowComplexity.countries.length > 0 && (
                <div className="kpi-divider" style={{ borderTopColor: 'var(--kpi-divider)' }}>
                  <div className="space-y-1.5">
                    {calculateECIKPIs.lowComplexity.countries.map((country, idx) => (
                      <div key={idx} className="flex justify-between items-center text-sm">
                        <span className="text-gray-700 dark:text-gray-300">{country.id}</span>
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {(country.avg_import_share || 0).toFixed(1)}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {!calculateECIKPIs.lowComplexity.hasImportData && (
                <div className="text-gray-500 dark:text-gray-400 text-xs mt-2">
                  Import share data not available
                </div>
              )}
            </div>

            {/* KPI 3: High-ECI Nation Share */}
            <div className="kpi-card" style={{ borderColor: 'var(--kpi-border)' }}>
              <div className="mb-3">
                <div className="kpi-label" style={{ color: 'var(--kpi-text)' }}>High-ECI Nation Share</div>
              </div>
              
              <div className="kpi-value">
                {calculateECIKPIs.highECI.percentage}%
              </div>
              
              <div className="kpi-metric" style={{ color: 'var(--kpi-text)' }}>
                {calculateECIKPIs.highECI.hasImportData 
                  ? `From ECI &gt;${calculateECIKPIs.highECI.threshold} economies`
                  : `No import data available`
                }
              </div>
              
              {/* Top high-ECI suppliers */}
              {calculateECIKPIs.highECI.hasImportData && calculateECIKPIs.highECI.countries.length > 0 && (
                <div className="kpi-divider" style={{ borderTopColor: 'var(--kpi-divider)' }}>
                  <div className="space-y-1.5">
                    {calculateECIKPIs.highECI.countries.map((country, idx) => (
                      <div key={idx} className="flex justify-between items-center text-sm">
                        <span className="text-gray-700 dark:text-gray-300">{country.id}</span>
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {(country.avg_import_share || 0).toFixed(1)}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {!calculateECIKPIs.highECI.hasImportData && (
                <div className="text-gray-500 dark:text-gray-400 text-xs mt-2">
                  Import share data not available
                </div>
              )}
            </div>
          </div>
        )}

        {/* Enhanced Controls - All in one line */}
        <div className="flex flex-col lg:flex-row lg:items-end gap-4">
          {/* Mineral Selection */}
          <div className="flex-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 block">
              Select Minerals: 
              <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                ({selectedMinerals.length} selected)
              </span>
            </label>
            <div className="flex flex-wrap gap-2">
              {availableMinerals.map((mineral, index) => {
                const isSelected = selectedMinerals.includes(mineral);
                
                return (
                  <button
                    key={mineral}
                    onClick={() => handleMineralToggle(mineral)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 border ${
                      isSelected 
                        ? 'text-white bg-blue-600 border-blue-600 shadow-lg transform scale-105' 
                        : 'text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                    }`}
                  >
                    {mineral}
                    {isSelected && selectedMinerals.length > 1 && (
                      <span className="ml-1 text-xs opacity-75">✓</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Display and Partner Controls - Aligned with mineral selection */}
          <div className="flex items-center gap-6">
            {/* Display Controls */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Display:</label>
              <select
                value={displayMode}
                onChange={(e) => setDisplayMode(e.target.value)}
                className="px-3 py-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
              >
                <option value="rank">Rank Position</option>
                <option value="score">ECI Score</option>
              </select>
            </div>

            {/* Partner Controls */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Partners:</label>
              <select
                value={partnerLimit}
                onChange={(e) => setPartnerLimit(e.target.value)}
                className="px-3 py-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
              >
                <option value="top-10">Top 10</option>
                <option value="all">All Partners</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="p-6">
        {error && (
          <div className="mb-4 p-4 bg-red-100 dark:bg-red-900 border border-red-300 dark:border-red-700 rounded-lg transition-colors duration-200">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              <span className="text-red-700 dark:text-red-200">
                Error: {error}
              </span>
            </div>
            <div className="mt-2 text-sm text-red-600 dark:text-red-300">
              Please ensure the backend server is running and the database contains ECI ranking data.
            </div>
          </div>
        )}
        
        <div className="flex gap-2 items-start">
          {/* ECI Color Legend - Left Side */}
          <div className="flex-shrink-0 w-24 mt-10">
            <div className="p-2 h-full">
              <div className="text-center mb-2">
                {/* <h4 className="text-sm font-semibold text-white mb-2">
                  {displayMode === 'rank' ? 'ECI Ranking Legend' : 'ECI Score Legend'}
                </h4> */}
                {/* <p className="text-xs text-gray-300">
                  {displayMode === 'rank' ? 'Lower = Better Ranking' : 'Higher = More Complex'}
                </p> */}
              </div>
              
              {/* Color Scale */}
              <div className="relative h-96 flex flex-col">
                {/* Numerical Scale */}
                <div className="absolute right-0 top-0 h-full flex flex-col justify-between text-xs text-gray-600 dark:text-gray-300">
                  {displayMode === 'rank' ? (
                    <>
                      <span>1</span>
                      <span>10</span>
                      <span>20</span>
                      <span>30</span>
                      <span>40</span>
                      <span>50</span>
                      <span>60</span>
                      <span>70</span>
                      <span>80</span>
                      <span>90</span>
                      <span>100</span>
                    </>
                  ) : (
                    <>
                      <span>2.5</span>
                      <span>2.0</span>
                      <span>1.5</span>
                      <span>1.0</span>
                      <span>0.5</span>
                      <span>0.0</span>
                      <span>-0.5</span>
                      <span>-1.0</span>
                      <span>-1.5</span>
                      <span>-2.0</span>
                      <span>-2.5</span>
                    </>
                  )}
                </div>
                
                {/* Color Bar */}
                <div className="absolute right-8 top-0 w-6 h-full rounded">
                  <div className="h-full w-full rounded" style={{
                    background: 'linear-gradient(to bottom, #2b54ac 0%, #95aae9 20%, #f5f4fc	 40%, #d6e2bc 60%, #f59e0b 80%, #df4f44	 100%)'
                  }}></div>
                </div>
                
                {/* Labels */}
                <div className="absolute left-0 top-0 h-full flex flex-col justify-between">
                  <div className="flex items-center">
                    
                    <span className="text-xs text-gray-900 dark:text-white transform -rotate-90 origin-left whitespace-nowrap" style={{marginTop:100}}>
                      {displayMode === 'rank' ? 'BETTER RANKING' : 'MORE COMPLEX'}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-xs text-gray-900 dark:text-white transform -rotate-90 origin-left whitespace-nowrap">
                      {displayMode === 'rank' ? 'WORSE RANKING' : 'LESS COMPLEX'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>


          {/* Chart */}
          <div style={{ height: '500px', flex: 1 }}>
            {processedData && processedData.length > 0 ? (
              <ResponsiveBump
                data={orderedData}
                colors={(serie) => serie.color}
                lineWidth={3}
                activeLineWidth={5}
                inactiveLineWidth={2}
                inactiveOpacity={0.3}
                pointSize={8}
                activePointSize={12}
                inactivePointSize={6}
                pointColor={{ theme: 'background' }}
                pointBorderWidth={2}
                activePointBorderWidth={3}
                pointBorderColor={{ from: 'serie.color' }}
                startLabel={false}
                endLabel="id"
                endLabelOffset={20}
                endLabelTextColor={isDarkMode ? "#ffffff" : "#1f2937"}
                xScale={{ 
                  type: 'band', 
                  padding: 0.1,
                  domain: allYears
                }}
                yScale={{ 
                  type: 'linear', 
                  min: 'auto', 
                  max: 'auto',
                  reverse: displayMode === 'score' // Reverse order for ECI Score (higher at top)
                }}
                axisTop={null}
                axisBottom={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: 'Year',
                  legendPosition: 'middle',
                  legendOffset: 32,
                  tickColor: isDarkMode ? '#9ca3af' : '#6b7280',
                  legendTextColor: isDarkMode ? '#ffffff' : '#1f2937',
                  format: (value) => value,
                  tickFormat: (value) => value,
                  tickValues: allYears,
                  scale: { type: 'point', padding: 0.1 }
                }}
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: displayMode === 'rank' ? 'ECI Ranking (Lower = Better)' : 'ECI Score (Higher = Better)',
                  legendPosition: 'middle',
                  legendOffset: -40,
                  tickColor: isDarkMode ? '#9ca3af' : '#6b7280',
                  legendTextColor: isDarkMode ? '#ffffff' : '#1f2937',
                  format: displayMode === 'score' ? (value) => (-value).toFixed(1) : undefined
                }}
                axisRight={null}
                margin={{ top: 40, right: 200, bottom: 60, left: 60 }}
                isInteractive={true}
                useMesh={true}
                animate={false}
                enableSlices={false}
                tooltip={({ point }) => {
                  // ResponsiveBump provides: point.serie.id, point.data.x, point.data.y
                  const country = point.serie.id;
                  const year = point.data.x;
                  const yValue = point.data.y;
                  
                  // Get the actual y value (handle negation for score mode)
                  const actualValue = displayMode === 'score' ? -yValue : yValue;
                  const formattedValue = displayMode === 'score' 
                    ? actualValue.toFixed(2) 
                    : Math.round(actualValue);
                  
                  return (
                    <div className={`${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'} border rounded-lg p-3 shadow-lg transition-colors duration-200`}>
                      <div className={`${isDarkMode ? 'text-white' : 'text-gray-900'} font-semibold text-sm mb-1`}>
                        {country}
                      </div>
                      <div className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} text-sm`}>
                        Year: <span className={`${isDarkMode ? 'text-white' : 'text-gray-900'} font-semibold`}>{year}</span>
                      </div>
                      <div className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} text-sm mt-1`}>
                        {displayMode === 'rank' ? 'Rank' : 'ECI Score'}: <span className={`${isDarkMode ? 'text-white' : 'text-gray-900'} font-semibold`}>{formattedValue}</span>
                      </div>
                    </div>
                  );
                }}
                theme={getNivoTheme(isDarkMode)}
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <AlertCircle className="h-12 w-12 text-gray-500 dark:text-gray-400 mx-auto mb-4" />
                  <div className="text-gray-600 dark:text-gray-300 mb-2">
                    {loading ? 'Loading ECI ranking data...' : 'No ECI ranking data available'}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {loading ? 'Please wait while we fetch the data from the backend' : 'Please check if the backend server is running and contains ECI data'}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

export default ECIBumpChart;
